import React from 'react';
import { Layout } from '@/components/layout/Layout';
import { elevageContent, images, aboutContent } from '@/data/mock';
import { MapPin, Award, Heart, Shield, Leaf, Users } from 'lucide-react';

const ElevagePage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion',
      description: "L'amour des félins guide chacune de nos décisions."
    },
    {
      icon: Shield,
      title: 'Santé',
      description: 'Tests ADN et suivi vétérinaire pour tous nos chats.'
    },
    {
      icon: Users,
      title: 'Socialisation',
      description: 'Chatons élevés en famille pour un tempérament équilibré.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Sélection rigoureuse pour des lignées exceptionnelles.'
    },
    {
      icon: Leaf,
      title: 'Bien-être',
      description: 'Environnement naturel et préservé en Bretagne.'
    }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block text-sm uppercase tracking-wider text-[var(--color-gold)] font-medium mb-4">
              Bienvenue chez nous
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--text-primary)] mb-6">
              {elevageContent.title}
            </h1>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto mb-8"></div>
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
              {elevageContent.description}
            </p>
          </div>
        </div>
      </section>

      {/* Location Image */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={images.brittany1}
                alt="Paysage de Bretagne"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src={images.brittany2}
                alt="Paysage de Bretagne - Phare"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
          <div className="flex items-center justify-center gap-3 mt-6 text-[var(--text-subtle)]">
            <MapPin className="w-5 h-5" />
            <span>Plounéour-Ménez, Monts d'Arrée, Finistère</span>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-24 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-sm uppercase tracking-wider text-[var(--color-gold)] font-medium mb-4">
                Qui sommes-nous
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] mb-6">
                Liza & Paol
              </h2>
              <div className="w-24 h-px bg-[var(--color-gold)] mb-8"></div>
              <p className="text-lg text-[var(--text-secondary)] leading-relaxed mb-6">
                Nous sommes Liza et Paol, titulaires de l'ACACED et passionnés par le monde félin depuis de nombreuses années.
              </p>
              <p className="text-[var(--text-secondary)] leading-relaxed mb-6">
                À travers Maison Bengal & Coon, nous avons fait le choix d'un élevage responsable, fondé sur le respect, le bien-être et la qualité de chaque chat que nous élevons.
              </p>
              <div className="flex items-center gap-4 p-4 bg-white rounded-xl">
                <Award className="w-10 h-10 text-[var(--color-gold)]" />
                <div>
                  <p className="font-medium text-[var(--text-primary)]">
                    Titulaires ACACED
                  </p>
                  <p className="text-sm text-[var(--text-subtle)]">
                    Attestation de Connaissances pour les Animaux de Compagnie d'Espèces Domestiques
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={images.maineCoon1}
                    alt="Maine Coon"
                    className="w-full aspect-square object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={images.maineCoon2}
                    alt="Maine Coon"
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={images.maineCoon3}
                    alt="Maine Coon"
                    className="w-full aspect-[3/4] object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={images.maineCoon4}
                    alt="Maine Coon"
                    className="w-full aspect-square object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] mb-4">
              Notre Élevage en Chiffres
            </h2>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {aboutContent.highlights.map((stat, index) => (
              <div
                key={index}
                className="text-center p-8 bg-[var(--surface-cream)] rounded-2xl hover-lift"
              >
                <div className="text-5xl font-serif font-medium text-[var(--color-gold)] mb-3">
                  {stat.number}
                </div>
                <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">
                  {stat.label}
                </h3>
                <p className="text-sm text-[var(--text-subtle)]">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[var(--surface-cream)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] mb-4">
              Nos Valeurs
            </h2>
            <div className="w-24 h-px bg-[var(--color-gold)] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-xl hover-lift">
                <div className="feature-icon mx-auto mb-4">
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="font-medium text-[var(--text-primary)] mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-[var(--text-subtle)]">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Royal Canin Partnership */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 sm:p-12 bg-[var(--surface-cream)] rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex-1 text-center md:text-left">
                <span className="text-sm uppercase tracking-wider text-[var(--color-gold)] font-medium">
                  Partenariat Officiel
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[var(--text-primary)] mt-2 mb-4">
                  Une alimentation d'excellence
                </h3>
                <p className="text-[var(--text-secondary)] leading-relaxed">
                  Nous travaillons en partenariat avec Royal Canin pour garantir 
                  une nutrition optimale à tous nos chats, de la naissance à l'âge adulte.
                  Un suivi régulier et des échanges permanents nous permettent d'optimiser 
                  la qualité des apports nutritionnels.
                </p>
              </div>
              <div className="flex-shrink-0">
                <img
                  src={images.royalCanin}
                  alt="Royal Canin - Partenaire"
                  className="h-20 sm:h-24 w-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ElevagePage;
